import {isLoggedIn} from '@github-ui/client-env'
import {IssueMarkdownViewer} from '@github-ui/commenting/IssueMarkdownViewer'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ReactionViewerAnchor} from '@github-ui/reaction-viewer/ReactionViewerAnchor'
import type {ReactionViewerRelayGroupsFragment} from '@github-ui/reaction-viewer/ReactionViewerRelay'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerSecondaryViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewQuery'
import {AddSubIssueButtonGroup} from '@github-ui/sub-issues/AddSubIssueButtonGroup'
import {useSubIssuesPresence} from '@github-ui/sub-issues/SubIssuesPresenceContext'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import type {TaskItem} from '@github-ui/use-tasklist'
import {useSafeTimeout} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useMemo} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {ERRORS} from '../constants/errors'
import {commitCreateIssueFromChecklistItemMutation} from '../mutations/create-issue-from-checklist-item-mutation'
import {commitCreateSubIssueFromChecklistItemMutation} from '../mutations/create-sub-issue-from-checklist-item-mutation'
import {commitUpdateIssueBodyMutation} from '../mutations/update-issue-body-mutation'
import type {IssueBodyViewer$key} from './__generated__/IssueBodyViewer.graphql'
import type {IssueBodyHeaderActionsNode} from './issue-body-header-actions-query-types'
import styles from './IssueBodyViewer.module.css'
import {PinnedCommentDisplay, type PinnedCommentDisplayFragment} from './PinnedCommentDisplay'

const ReactionViewerRelay = React.lazy(() => import('@github-ui/reaction-viewer/ReactionViewerRelay'))

// RELAY-CLEANUP(IssueBody): widens nullable/undefined fields so the still-Relay parent data is
// assignable. Remove once IssueBody leaves Relay.
export type IssueBodyViewerSubIssuesFragment = RelayCompat<
  Pick<IssueBodyHeaderActionsNode, 'id' | 'databaseId' | 'number' | 'viewerCanUpdateMetadata'> & {
    readonly repository: Pick<
      IssueBodyHeaderActionsNode['repository'],
      'isArchived' | 'nameWithOwner' | 'visibility' | 'viewerCanPush'
    > & {
      readonly owner: Pick<IssueBodyHeaderActionsNode['repository']['owner'], 'login'>
    }
    readonly subIssuesSummary: Pick<IssueBodyHeaderActionsNode['subIssuesSummary'], 'total'>
  }
>

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay parent
// data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type IssueBodyViewerPinnedCommentSecondaryFragment = RelayCompat<
  Pick<NonNullable<NonNullable<IssueViewerSecondaryViewQueryQuery['repository']>['issue']>, 'pinnedIssueComment'>
>

// RELAY-CLEANUP(IssueBodyViewerReactable): uses the child's type directly since the fragment
// was on the `Reactable` interface. Remove the re-export when IssueBody leaves Relay.
export type IssueBodyViewerReactableFragment = ReactionViewerRelayGroupsFragment

export type IssueBodyViewerProps = {
  html: SafeHTMLString
  markdown: string
  comment: IssueBodyViewer$key
  onLinkClick?: (event: MouseEvent) => void
  issueBodyRef?: React.RefObject<HTMLDivElement | null>
  bodyVersion: string
  locked: boolean
  reactable: IssueBodyViewerReactableFragment
  subIssues?: IssueBodyViewerSubIssuesFragment | null
  pinnedComment?: PinnedCommentDisplayFragment | null
  pinnedCommentSecondary?: IssueBodyViewerPinnedCommentSecondaryFragment | null
  insideSidePanel?: boolean
  repositoryId?: string
  onIssueEditStateChange?: (edited: boolean) => void
  isReadonly: boolean
  viewerCanCreateIssues: boolean
  viewerCanUpdate: boolean
  issueBodyTaskListAction?: React.ReactNode
}

export function IssueBodyViewer({
  html,
  markdown,
  comment,
  onLinkClick,
  issueBodyRef,
  bodyVersion,
  locked,
  reactable,
  subIssues: dataSubIssues = null,
  pinnedComment = null,
  pinnedCommentSecondary = null,
  insideSidePanel,
  repositoryId,
  onIssueEditStateChange,
  isReadonly,
  viewerCanCreateIssues,
  viewerCanUpdate,
  issueBodyTaskListAction,
}: IssueBodyViewerProps) {
  const environment = useRelayEnvironment()
  const {safeSetTimeout, safeClearTimeout} = useSafeTimeout()
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyViewer on Comment {
        id
      }
    `,
    comment,
  )
  const primaryHasSubIssues = useHasSubIssues(dataSubIssues)
  // Read the section's overlaid presence (via context) so this empty-state button and the
  // section stay complementary (exactly one shows). Falls back to the Relay summary otherwise.
  const overlaidHasSubIssues = useSubIssuesPresence()
  const hasSubIssues = overlaidHasSubIssues ?? primaryHasSubIssues
  const canEditSubIssues = useCanEditSubIssues(dataSubIssues)
  const showAddSubIssueButton = canEditSubIssues && dataSubIssues && !hasSubIssues && !isReadonly
  const showIssueBodyTaskListActions = Boolean(showAddSubIssueButton || issueBodyTaskListAction)

  const onSave = useCallback(
    (newBody: string, onCompleted: () => void, onError: () => void) => {
      commitUpdateIssueBodyMutation({
        environment,
        input: {issueId: data.id, body: newBody, bodyVersion},
        onCompleted,
        onError,
      })
    },
    [bodyVersion, environment, data.id],
  )

  const {addToast} = useToastContext()

  /**
   * * Handles conversion completion timing - can't immediately set isConverting to false
   * because UI updates (rendering converted items as links) happen asynchronously.
   * Uses MutationObserver to wait for DOM changes ensuring loading state persists
   * until visual conversion is complete.
   **/
  const handleConversionComplete = useCallback(
    (
      id: string,
      setIsConverting: (isConverting: boolean) => void,
      onCompletedCallback?: () => void,
      convertingToSubIssue = false,
    ) => {
      let timeoutId: ReturnType<typeof safeSetTimeout> | null = null

      const completeMutation = () => {
        setIsConverting(false)
        onCompletedCallback?.()

        if (timeoutId) {
          safeClearTimeout(timeoutId)
        }
      }

      if (convertingToSubIssue) {
        // No need for observation as the rerender is triggered immediately
        requestAnimationFrame(() => {
          completeMutation()
        })
        return
      }

      const observer = new MutationObserver(mutationsList => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const safeHTMLBox = document.querySelector(`#checkbox-item-${id}`)

            if (safeHTMLBox?.querySelector('a')) {
              observer.disconnect()
              completeMutation()
              return
            }
          }
        }
      })

      // Observing the closest element that doesn't rerender on mutation complete
      const issueContainer = document.getElementById('issue-body-viewer')
      if (issueContainer) {
        observer.observe(issueContainer, {childList: true, subtree: true})
      } else {
        completeMutation()
        return
      }

      // If we don't find the newly inserted link, disconnect
      timeoutId = safeSetTimeout(() => {
        observer.disconnect()
        completeMutation()
      }, 3000)
    },
    [safeSetTimeout, safeClearTimeout],
  )

  const onConvertToIssue = useCallback(
    (
      task: TaskItem,
      setIsConverting: (converting: boolean) => void,
      onCompletedCallback?: () => void,
      onErrorCallback?: (error: Error) => void,
    ) => {
      if (!(repositoryId && onIssueEditStateChange)) return

      setIsConverting(true)
      commitCreateIssueFromChecklistItemMutation({
        environment,
        input: {parentIssueId: data.id, repositoryId, title: task.title, position: task.position},
        onCompleted: () => {
          onIssueEditStateChange?.(true)
          handleConversionComplete(task.id, setIsConverting, onCompletedCallback)
        },
        onError: error => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: ERRORS.couldNotConvertIssue})
          setIsConverting(false)
          onErrorCallback?.(error)
        },
      })
    },
    [repositoryId, onIssueEditStateChange, environment, data.id, addToast, handleConversionComplete],
  )

  const onConvertToSubIssue = useMemo(
    () =>
      (
        task: TaskItem,
        setIsConverting: (converting: boolean) => void,
        onCompletedCallback?: () => void,
        onErrorCallback?: (error: Error) => void,
      ) => {
        if (!(repositoryId && onIssueEditStateChange)) return

        setIsConverting(true)
        commitCreateSubIssueFromChecklistItemMutation({
          environment,
          input: {
            parentIssueId: data.id,
            repositoryId,
            body: markdown,
            position: task.position as [number, number],
          },
          onCompleted: () => {
            onIssueEditStateChange?.(true)
            handleConversionComplete(task.id, setIsConverting, onCompletedCallback, true)
          },
          onError: error => {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({type: 'error', message: ERRORS.couldNotConvertIssue})
            setIsConverting(false)
            onErrorCallback?.(error)
          },
        })
      },
    [repositoryId, onIssueEditStateChange, environment, data.id, addToast, markdown, handleConversionComplete],
  )

  const modernUx = isFeatureEnabled('issues_modern_ux')

  return (
    <div
      className={clsx(styles.IssueBody, modernUx && styles.IssueBodyModernUx)}
      ref={issueBodyRef}
      id="issue-body-viewer"
      data-testid="issue-body-viewer"
    >
      <IssueMarkdownViewer
        html={html}
        markdown={markdown}
        onSave={onSave}
        onLinkClick={onLinkClick}
        onConvertToIssue={onConvertToIssue}
        onConvertToSubIssue={onConvertToSubIssue}
        viewerCanCreateIssues={viewerCanCreateIssues}
        viewerCanUpdate={viewerCanUpdate}
      />
      <div className={styles.IssueBodyTaskList}>
        {showIssueBodyTaskListActions && (
          <div className={styles.IssueBodySubIssueButtonContainer}>
            {showAddSubIssueButton ? (
              <AddSubIssueButtonGroup
                issue={dataSubIssues}
                insideSidePanel={insideSidePanel}
                leadingAction={issueBodyTaskListAction}
              />
            ) : (
              issueBodyTaskListAction
            )}
          </div>
        )}
        {!isReadonly && (
          <Suspense fallback={<ReactionViewerAnchor />}>
            <ReactionViewerRelay subjectId={data.id} canReact={isLoggedIn() && !locked} reactionGroups={reactable} />
          </Suspense>
        )}
      </div>
      {pinnedComment && <PinnedCommentDisplay issue={pinnedComment} secondary={pinnedCommentSecondary} />}
    </div>
  )
}
